import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { BonusesService } from '@atlas/bonuses';
import { useApplicationState, useSettings } from '@atlas/core';

import requestBuilder from 'src/common/utils/requestBuilder';

/**
 * Module-level singleton cache keyed by "{country}_{currency}_{lang}".
 */
const cache = new Map();

/**
 * Tracks reactive version per service instance.
 * WeakMap ensures automatic cleanup when a service instance is garbage collected.
 */
const versions = new WeakMap();

const bumpVersion = service => {
  versions.set(service, (versions.get(service) || 0) + 1);
};

/**
 * Hook that provides a shared BonusesService singleton with reactive updates.
 *
 * Why useSyncExternalStore:
 *   BonusesService is an external (non-React) store — it manages its own state
 *   internally and emits 'data' events when bonuses change. React has no way
 *   to know about these mutations. useSyncExternalStore is the official React 18
 *   API for subscribing to external data sources. It guarantees:
 *   - Consistent reads during concurrent renders (no tearing)
 *   - Automatic re-render when the snapshot changes
 *   - Correct behavior with React.StrictMode and Suspense
 *
 * How it works:
 *   1. A singleton BonusesService is created and cached by (country, currency, lang) key
 *   2. On each store event, a version counter is incremented → snapshot changes → React re-renders
 *   3. After re-render, all service getters (activeBonuses, archivedBonuses, etc.)
 *      return fresh data from the mutated internal state
 *
 * @returns {BonusesService | null} Shared service instance, or null if not authenticated
 *
 * @example
 *   const service = useBonusesService();
 *
 *   // All getters are reactive — component re-renders when data updates
 *   service?.activeBonuses
 *   service?.archivedBonuses
 *   service?.nonExpiredBonuses
 *   service?.clientBonuses
 *
 *   // Call methods directly
 *   service?.load()
 *   service?.cancelBonus(id)
 */
const useBonusesService = () => {
  const { account, authenticated, wallet } = useApplicationState();
  const { lang } = useSettings();

  // Singleton: returns the same instance for the same params across all components
  const service = useMemo(() => {
    const currency = wallet?.currency || account?.currency;

    if (!currency || !lang || !authenticated) {
      return null;
    }

    const key = `${account?.country}_${currency}_${lang}`;

    if (cache.has(key)) {
      return cache.get(key);
    }

    const instance = new BonusesService({
      country: account?.country || null,
      currency,
      language: lang,
      requestBuilder,
    });

    versions.set(instance, 0);

    cache.set(key, instance);

    return instance;
  }, [account?.country, account?.currency, authenticated, lang, wallet?.currency]);

  const subscribe = useCallback(
    onStoreChange => {
      if (!service) {
        return () => {};
      }

      const handler = () => {
        bumpVersion(service);
        onStoreChange();
      };

      service.on('data', handler);
      service.on('loading', handler);
      service.on('error', handler);

      return () => {
        service.off('data', handler);
        service.off('loading', handler);
        service.off('error', handler);
      };
    },
    [service],
  );

  const getSnapshot = useCallback(() => {
    if (!service) {
      return 0;
    }

    return versions.get(service) || 0;
  }, [service]);

  useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return service;
};

export default useBonusesService;
